/**
 * Cross-Device Comparison Engine
 * Analyzes and compares findings between desktop and mobile viewports
 * Identifies shared issues, device-specific problems, and comparison insights
 */

import type { AuditFinding, DeviceType, SeverityLevel } from '@/types/audit';

// ============================================
// Types
// ============================================

export interface DeviceFindings {
  desktop: AuditFinding[];
  mobile: AuditFinding[];
}

export interface ComparisonResult {
  /** Issues that appear on both devices (matched by principleId) */
  sharedIssues: SharedIssue[];
  /** Issues only found on desktop */
  desktopOnlyIssues: AuditFinding[];
  /** Issues only found on mobile */
  mobileOnlyIssues: AuditFinding[];
  /** Summary statistics */
  summary: ComparisonSummary;
  /** Insights and recommendations */
  insights: ComparisonInsight[];
}

export interface SharedIssue {
  principleId: string;
  principleTitle: string;
  /** Highest severity between the two devices */
  severity: SeverityLevel;
  desktop: AuditFinding;
  mobile: AuditFinding;
  /** Whether the severity differs between devices */
  severityDiffers: boolean;
}

export interface ComparisonSummary {
  totalIssues: number;
  sharedCount: number;
  desktopOnlyCount: number;
  mobileOnlyCount: number;
  /** Percentage of issues that are shared (0-100) */
  sharedPercentage: number;
  /** Device with more issues */
  moreProblematicDevice: DeviceType | 'equal';
  /** Severity breakdown by device */
  severityByDevice: {
    desktop: SeverityCounts;
    mobile: SeverityCounts;
  };
}

export interface SeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

export interface ComparisonInsight {
  type: 'warning' | 'info' | 'success';
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
}

// ============================================
// Utility Functions
// ============================================

/**
 * Calculate severity counts from findings
 */
function countSeverities(findings: AuditFinding[]): SeverityCounts {
  return findings.reduce(
    (acc, f) => {
      acc[f.severity]++;
      acc.total++;
      return acc;
    },
    { critical: 0, high: 0, medium: 0, low: 0, total: 0 }
  );
}

/**
 * Get the higher severity between two levels
 */
function getHigherSeverity(a: SeverityLevel, b: SeverityLevel): SeverityLevel {
  const order: Record<SeverityLevel, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
  };
  return order[a] >= order[b] ? a : b;
}

/**
 * Calculate similarity between two strings (Jaccard index)
 */
function stringSimilarity(a: string, b: string): number {
  const setA = new Set(a.toLowerCase().split(/\s+/));
  const setB = new Set(b.toLowerCase().split(/\s+/));

  const intersection = new Set([...setA].filter(x => setB.has(x)));
  const union = new Set([...setA, ...setB]);

  return intersection.size / union.size;
}

// ============================================
// Main Comparison Function
// ============================================

/**
 * Compare findings between desktop and mobile viewports
 */
export function compareDeviceFindings(findings: DeviceFindings): ComparisonResult {
  const { desktop, mobile } = findings;

  // Track which findings have been matched, BY POSITION. This used to be keyed on
  // `principleId + description.substring(0, 50)`. Two findings on the same device
  // that agreed on the id and the first 50 characters produced one key, so marking
  // one matched marked both, and the unmatched one was dropped from the report
  // instead of surviving as device-specific. Indices cannot collide.
  const matchedDesktop = new Set<number>();
  const matchedMobile = new Set<number>();

  const sharedIssues: SharedIssue[] = [];

  // First pass: Match by principleId (exact match). An empty id is a finding
  // whose citation could not be resolved (principle-ids.ts), not a shared key:
  // two such findings are not the same issue.
  desktop.forEach((desktopFinding, desktopIndex) => {
    if (!desktopFinding.principleId) return;
    const mobileIndex = mobile.findIndex(
      (m, i) => !matchedMobile.has(i) && m.principleId === desktopFinding.principleId
    );
    if (mobileIndex === -1) return;

    const matchingMobile = mobile[mobileIndex];
    matchedDesktop.add(desktopIndex);
    matchedMobile.add(mobileIndex);

    sharedIssues.push({
      principleId: desktopFinding.principleId,
      principleTitle: desktopFinding.principleTitle,
      severity: getHigherSeverity(desktopFinding.severity, matchingMobile.severity),
      desktop: desktopFinding,
      mobile: matchingMobile,
      severityDiffers: desktopFinding.severity !== matchingMobile.severity,
    });
  });

  // Second pass: Match by similar descriptions (for different principle IDs)
  desktop.forEach((desktopFinding, desktopIndex) => {
    if (matchedDesktop.has(desktopIndex)) return;

    for (let mobileIndex = 0; mobileIndex < mobile.length; mobileIndex++) {
      if (matchedMobile.has(mobileIndex)) continue;
      const mobileFinding = mobile[mobileIndex];

      // Check if descriptions are similar (>70% similarity)
      const similarity = stringSimilarity(desktopFinding.description, mobileFinding.description);
      if (similarity > 0.7) {
        matchedDesktop.add(desktopIndex);
        matchedMobile.add(mobileIndex);

        sharedIssues.push({
          principleId: desktopFinding.principleId,
          principleTitle: desktopFinding.principleTitle,
          severity: getHigherSeverity(desktopFinding.severity, mobileFinding.severity),
          desktop: desktopFinding,
          mobile: mobileFinding,
          severityDiffers: desktopFinding.severity !== mobileFinding.severity,
        });
        break;
      }
    }
  });

  // Collect unmatched findings
  const desktopOnlyIssues = desktop.filter((_, i) => !matchedDesktop.has(i));
  const mobileOnlyIssues = mobile.filter((_, i) => !matchedMobile.has(i));

  // Calculate summary
  const totalIssues = sharedIssues.length + desktopOnlyIssues.length + mobileOnlyIssues.length;
  const sharedPercentage = totalIssues > 0
    ? Math.round((sharedIssues.length / totalIssues) * 100)
    : 0;

  let moreProblematicDevice: DeviceType | 'equal' = 'equal';
  if (desktop.length > mobile.length) {
    moreProblematicDevice = 'desktop';
  } else if (mobile.length > desktop.length) {
    moreProblematicDevice = 'mobile';
  }

  const summary: ComparisonSummary = {
    totalIssues,
    sharedCount: sharedIssues.length,
    desktopOnlyCount: desktopOnlyIssues.length,
    mobileOnlyCount: mobileOnlyIssues.length,
    sharedPercentage,
    moreProblematicDevice,
    severityByDevice: {
      desktop: countSeverities(desktop),
      mobile: countSeverities(mobile),
    },
  };

  // Generate insights
  const insights = generateInsights(summary, sharedIssues, desktopOnlyIssues, mobileOnlyIssues);

  return {
    sharedIssues,
    desktopOnlyIssues,
    mobileOnlyIssues,
    summary,
    insights,
  };
}

// ============================================
// Insight Generation
// ============================================

function generateInsights(
  summary: ComparisonSummary,
  sharedIssues: SharedIssue[],
  desktopOnlyIssues: AuditFinding[],
  mobileOnlyIssues: AuditFinding[]
): ComparisonInsight[] {
  const insights: ComparisonInsight[] = [];

  // High shared percentage insight
  if (summary.sharedPercentage >= 70) {
    insights.push({
      type: 'info',
      title: 'Consistent Issues Across Devices',
      description: `${summary.sharedPercentage}% of issues appear on both devices. Fixing these will improve the experience for all users.`,
      priority: 'high',
    });
  }

  // Mobile-specific issues warning
  const mobileCritical = mobileOnlyIssues.filter((f) => f.severity === 'critical').length;
  if (mobileCritical > 0) {
    insights.push({
      type: 'warning',
      title: 'Critical Mobile Issues',
      description: `Found ${mobileCritical} critical issue${mobileCritical > 1 ? 's' : ''} affecting only mobile users. These should be prioritized as mobile traffic typically accounts for 50%+ of visitors.`,
      priority: 'high',
    });
  }

  // Desktop-specific issues
  const desktopCritical = desktopOnlyIssues.filter((f) => f.severity === 'critical').length;
  if (desktopCritical > 0) {
    insights.push({
      type: 'warning',
      title: 'Critical Desktop Issues',
      description: `Found ${desktopCritical} critical issue${desktopCritical > 1 ? 's' : ''} affecting only desktop users. These may impact conversion rates for users on larger screens.`,
      priority: 'high',
    });
  }

  // Severity differs between devices
  const severityDiffers = sharedIssues.filter((s) => s.severityDiffers);
  if (severityDiffers.length > 0) {
    insights.push({
      type: 'info',
      title: 'Severity Varies by Device',
      description: `${severityDiffers.length} issue${severityDiffers.length > 1 ? 's' : ''} have different severity levels depending on the device. Consider the context-specific impact when prioritizing fixes.`,
      priority: 'medium',
    });
  }

  // Device balance
  if (summary.moreProblematicDevice === 'mobile') {
    const diff = summary.severityByDevice.mobile.total - summary.severityByDevice.desktop.total;
    insights.push({
      type: 'warning',
      title: 'Mobile Needs More Attention',
      description: `Mobile has ${diff} more issue${diff > 1 ? 's' : ''} than desktop. Review mobile-specific layouts and touch interactions.`,
      priority: 'medium',
    });
  } else if (summary.moreProblematicDevice === 'desktop') {
    const diff = summary.severityByDevice.desktop.total - summary.severityByDevice.mobile.total;
    insights.push({
      type: 'warning',
      title: 'Desktop Needs More Attention',
      description: `Desktop has ${diff} more issue${diff > 1 ? 's' : ''} than mobile. Check hover states, navigation patterns, and wide-screen layouts.`,
      priority: 'medium',
    });
  } else if (summary.totalIssues > 0) {
    insights.push({
      type: 'success',
      title: 'Balanced Experience',
      description: 'Both devices have similar issue counts, suggesting consistent attention to both experiences.',
      priority: 'low',
    });
  }

  // No issues found
  if (summary.totalIssues === 0) {
    insights.push({
      type: 'success',
      title: 'No Issues Detected',
      description: 'No UX issues were found on either device. Great work maintaining a consistent, high-quality experience!',
      priority: 'low',
    });
  }

  // Sort by priority
  const priorityOrder = { high: 0, medium: 1, low: 2 };
  insights.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return insights;
}

// ============================================
// Helper Functions for UI
// ============================================

/**
 * Filter comparison results by view type
 */
export function filterByView(
  comparison: ComparisonResult,
  view: 'all' | 'shared' | 'desktop-only' | 'mobile-only'
): AuditFinding[] {
  switch (view) {
    case 'shared':
      // Return desktop findings for shared issues (they're equivalent)
      return comparison.sharedIssues.map((s) => s.desktop);
    case 'desktop-only':
      return comparison.desktopOnlyIssues;
    case 'mobile-only':
      return comparison.mobileOnlyIssues;
    case 'all':
    default:
      return [
        ...comparison.sharedIssues.map((s) => s.desktop),
        ...comparison.desktopOnlyIssues,
        ...comparison.mobileOnlyIssues,
      ];
  }
}

/**
 * Get the device badge for a finding in comparison context
 */
export function getComparisonDeviceBadge(
  finding: AuditFinding,
  comparison: ComparisonResult
): 'desktop' | 'mobile' | 'both' | null {
  // Check if it's a shared issue
  const isShared = comparison.sharedIssues.some(
    (s) =>
      s.desktop.principleId === finding.principleId ||
      s.mobile.principleId === finding.principleId
  );
  if (isShared) return 'both';

  // Check if desktop-only
  const isDesktopOnly = comparison.desktopOnlyIssues.some(
    (f) => f.principleId === finding.principleId && f.description === finding.description
  );
  if (isDesktopOnly) return 'desktop';

  // Check if mobile-only
  const isMobileOnly = comparison.mobileOnlyIssues.some(
    (f) => f.principleId === finding.principleId && f.description === finding.description
  );
  if (isMobileOnly) return 'mobile';

  return finding.device || null;
}

/**
 * Get severity color (for UI consistency)
 */
export function getSeverityColor(severity: SeverityLevel): string {
  const colors: Record<SeverityLevel, string> = {
    critical: '#EF4444',
    high: '#F97316',
    medium: '#F59E0B',
    low: '#22C55E',
  };
  return colors[severity];
}
